import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { nodesToPdf } from '../../utils/pdf';
import { getChecklist } from '../../config/documentChecklists';
import { IconUpload, IconFileText, IconChartLine } from '../../components/dashboardIcons';
import IndicatorTable from './IndicatorTable';
import SummaryTables, { hasSummaryTables } from './SummaryTables';
import Recommendations from './Recommendations';
import { reportChartFor } from './reportCharts';
import ElementIcon from '../../components/ElementIcon';
import { themeVars } from '../../config/theme';

const ESD_GROUP_KEY = 'enterprise_supplier_development';
const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);

const downloadBlob = async (url, fallbackName, onErr) => {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const disposition = res.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const href = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = href;
    a.download = match ? match[1] : fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  } catch (err) {
    onErr?.(err);
  }
};

const Tick = ({ done }) => (
  <span className={`cd-tick ${done ? '' : 'open'}`}>
    <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
  </span>
);

// ─── Documents ───────────────────────────────────────────────────────────────
const DocumentsPanel = ({ group }) => {
  const element = group.elements[0];
  const checklist = getChecklist(element);
  const fileRef = useRef(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [uploads, setUploads] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/core/documents/?element=${element}`)
      .then((res) => {
        const existing = {};
        res.data.forEach((doc) => {
          existing[doc.doc_type] = [...(existing[doc.doc_type] || []), doc.filename];
        });
        setUploads(existing);
      })
      .catch(() => {});
  }, [element]);

  const pick = (doc) => {
    setActiveDoc(doc);
    setError('');
    if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeDoc) return;
    setBusy(true); setError('');
    try {
      const names = [];
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        form.append('doc_type', activeDoc);
        form.append('element', element);
        await api.post('/core/documents/upload/', form);
        names.push(file.name);
      }
      setUploads((prev) => ({ ...prev, [activeDoc]: [...(prev[activeDoc] || []), ...names] }));
    } catch {
      setError('Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const done = checklist.filter((d) => uploads[d]?.length).length;

  return (
    <div>
      <p className="cd-muted" style={{ margin: '0 0 12px' }}>{done} of {checklist.length} document types uploaded</p>
      <div className="cd-doc-grid">
        {checklist.map((doc) => (
          <button
            key={doc}
            type="button"
            className={`cd-doc-card ${uploads[doc]?.length ? 'is-done' : ''}`}
            onClick={() => pick(doc)}
            disabled={busy}
          >
            <Tick done={!!uploads[doc]?.length} />
            <span className="lbl">{doc}</span>
            {uploads[doc]?.length > 0 && (
              <span className="files">{uploads[doc].map((n, i) => <span key={i}>✓ {n}</span>)}</span>
            )}
          </button>
        ))}
      </div>
      <input ref={fileRef} type="file" accept=".xlsx,.pdf,.docx,.jpg,.png" multiple hidden onChange={handleFiles} />
      {error && <div className="cd-error">{error}</div>}
    </div>
  );
};

// ─── Upload data sheet ───────────────────────────────────────────────────────
const UploadPanel = ({ group, onCalculated }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const primaryElement = group.elements[0];
  const isESD = group.key === ESD_GROUP_KEY;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      let res;
      if (isESD) {
        res = await api.post('/core/scorecard/calculate_esd/', form);
      } else {
        form.append('element', primaryElement);
        res = await api.post('/core/scorecard/calculate/', form);
      }
      const points = isESD
        ? (res.data.ed?.total_points ?? 0) + (res.data.sd?.total_points ?? 0)
        : res.data.total_points;
      setResult({ points, filename: file.name });
      onCalculated?.();
    } catch (err) {
      setError(err.response?.data?.error || 'The data sheet could not be processed. Check it matches the template and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" className="cd-btn cd-btn--ghost"
          onClick={() => downloadBlob(`/core/scorecard/download_template/?element=${primaryElement}`, `${group.label} template.xlsx`, () => setError('Could not download the template.'))}>
          Download blank template
        </button>
        <button type="button" className="cd-btn cd-btn--ghost"
          onClick={() => downloadBlob(`/core/scorecard/download_data_sheet/?element=${primaryElement}`, `${group.label} data sheet.xlsx`,
            (err) => setError(err.response?.status === 404 ? 'No data sheet uploaded yet.' : 'Could not download your last data sheet.'))}>
          Download my last sheet
        </button>
      </div>
      <button type="button" className="cd-btn cd-btn--primary" style={{ width: '100%' }} onClick={() => fileRef.current?.click()} disabled={busy}>
        {busy ? 'Processing…' : 'Upload completed sheet (.xlsx)'}
      </button>
      <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={handleFile} />
      {error && <div className="cd-error">{error}</div>}
      {result && <div className="cd-success">Recalculated from {result.filename}: {fmt(result.points)} points.</div>}
    </div>
  );
};

// ─── Report ──────────────────────────────────────────────────────────────────
const ReportPanel = ({ group, scorecards, companyName, financialYear, beeLevel }) => {
  const sheetRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const Chart = reportChartFor(group.key);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await nodesToPdf(sheetRef.current, `bbee-${group.key}-report.pdf`, { orientation: 'landscape' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <button type="button" className="cd-btn cd-btn--primary" onClick={handleDownload} disabled={downloading} style={{ marginBottom: 16 }}>
        {downloading ? 'Generating PDF…' : 'Download PDF'}
      </button>
      <div className="cd-report-sheet" ref={sheetRef}>
        <div className="rs-head">
          <h2>{companyName || 'B-BBEE Report'}</h2>
          <p>{financialYear?.label}</p>
          <p>{group.label}{beeLevel?.level ? ` · overall ${beeLevel.level}` : ''}</p>
        </div>
        {Chart ? (
          <div className="cd-report-embed"><Chart /></div>
        ) : (
          group.elements.map((id) => (
            <div key={id}>
              {group.elements.length > 1 && <h3 style={{ textTransform: 'capitalize' }}>{id.replace(/_/g, ' ')}</h3>}
              <IndicatorTable data={scorecards[id]} />
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Overview: scorecard summary + breakdown tables + actions ────────────────
const OverviewPanel = ({ group, score, onView }) => {
  const pct = score && score.maxPoints > 0
    ? Math.round((score.points / score.maxPoints) * 100)
    : null;

  return (
    <>
      <div className="cd-scorecard-summary">
        <div className="cd-sc-card">
          <span>Points achieved</span>
          <b>{score ? fmt(score.points) : '—'} <small>/ {score ? score.maxPoints : '—'}</small></b>
        </div>
        <div className="cd-sc-card">
          <span>Compliance</span>
          <b>{pct != null ? `${pct}%` : '—'}</b>
        </div>
        <div className="cd-sc-card">
          <span>Sub-minimum</span>
          <b className={score && score.subMinimumMet === false ? 'is-fail' : ''}>
            {!score ? '—' : score.subMinimumMet === false ? 'Not met' : 'Met'}
          </b>
        </div>
      </div>

      {group.elements.map((id) => {
        const d = group.__scorecards[id];
        const split = hasSummaryTables(d);
        return (
          <div key={id} className="cd-sc-block">
            {group.elements.length > 1 && (
              <div className="cd-blk" style={{ textTransform: 'capitalize' }}>{id.replace(/_/g, ' ')}</div>
            )}
            <div className={split ? 'cd-sc-split' : ''}>
              <div>
                <div className="cd-blk">Scorecard breakdown</div>
                <IndicatorTable data={d} />
              </div>
              {split && <div><SummaryTables data={d} /></div>}
            </div>
            <Recommendations data={d} />
          </div>
        );
      })}

      <div className="cd-panel-actions">
        <button type="button" className="cd-action primary" onClick={() => onView('upload')}>
          <IconUpload /> Upload data sheet
        </button>
        <button type="button" className="cd-action" onClick={() => onView('documents')}>
          <IconFileText /> Upload documents
        </button>
        <button type="button" className="cd-action" onClick={() => onView('report')}>
          <IconChartLine /> View report
        </button>
      </div>
    </>
  );
};

// ─── Panel shell ─────────────────────────────────────────────────────────────
const ElementPanel = ({ group, accent, score, scorecards, companyName, financialYear, beeLevel, initialView = 'overview', onClose, onCalculated }) => {
  // "scorecard" is shown by the overview itself.
  const [view, setView] = useState(initialView === 'scorecard' ? 'overview' : initialView);
  const primaryEl = group.elements[0];
  const filename = scorecards[primaryEl]?.filename;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (view === 'overview') onClose(); else setView('overview');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view, onClose]);

  const pct = score && score.maxPoints > 0
    ? Math.min(Math.round((score.points / score.maxPoints) * 100), 100)
    : null;
  const needsAction = !score || score.subMinimumMet === false;

  // scorecards passed through to OverviewPanel without prop drilling churn
  const groupWithCards = { ...group, __scorecards: scorecards };

  return (
    <div className="cd-panel themed" style={{ ...themeVars(group.key), '--a': accent }}>
      <div className="cd-panel-head">
        <ElementIcon element={group.key} />
        <div className="cd-panel-htext">
          <div className="nm">{group.label}</div>
          <div className="sub">{filename ? `Calculated from ${filename}` : 'Not calculated yet — upload a data sheet'}</div>
        </div>
        <div className="cd-panel-hmeta">
          <div className="pts">
            {score ? <>{fmt(score.points)}<small> / {score.maxPoints}{pct != null ? ` · ${pct}%` : ''}</small></> : '—'}
          </div>
          <span className={`cd-chip ${needsAction ? 'act' : 'ok'}`}>
            {!score ? 'No data yet' : score.subMinimumMet === false ? 'Action needed' : 'On track'}
          </span>
        </div>
        {view !== 'overview' && (
          <button type="button" className="cd-panel-back" onClick={() => setView('overview')}>← Back</button>
        )}
        <button type="button" className="cd-panel-x" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="cd-panel-body">
        {view === 'overview' && <OverviewPanel group={groupWithCards} score={score} onView={setView} />}
        {view === 'upload' && <UploadPanel group={group} onCalculated={onCalculated} />}
        {view === 'documents' && <DocumentsPanel group={group} />}
        {view === 'report' && (
          <ReportPanel group={group} scorecards={scorecards} companyName={companyName} financialYear={financialYear} beeLevel={beeLevel} />
        )}
      </div>
    </div>
  );
};

export default ElementPanel;
